import type { CollectionName } from "@/store/collections";

/**
 * Debug-mode read tracking for `useCollectionsCompute` dependency lists.
 *
 * Each compute pipeline hand-declares the collections it reads, and the version-sum snapshot
 * re-runs it only when a declared collection is written. An undeclared read is invisible to that
 * snapshot, so the view goes stale on that collection's writes. Recording reads during a compute
 * turns the drift into a warning in dev and a test failure under vitest.
 *
 * Nothing is installed in production: the store's read paths call `recordRead`, which returns on a
 * single null check.
 */

/**
 * Collections read since the enclosing `trackReads` began, or null when nothing is tracking.
 *
 * Typed over `PropertyKey` so `EntityStore`, generic over its own schema, can record `keyof Schema`
 * without a cast; `trackReads` installs (and hands back) a `CollectionName` set.
 */
let currentTracker: Set<PropertyKey> | null = null;

const warnedDrifts = new Set<string>();

/** True under vitest, where an undeclared read throws instead of warning. */
const throwOnUndeclaredRead = import.meta.env.MODE === "test";

/** Records a collection read against the active tracker; a no-op with none installed. */
export function recordRead(name: PropertyKey): void {
  if (currentTracker !== null) currentTracker.add(name);
}

/**
 * Runs `fn` with read tracking installed, returning its result alongside the collections it read.
 *
 * Nested calls each collect their own reads and restore the enclosing tracker on exit.
 */
export function trackReads<T>(fn: () => T): { result: T; collections: Set<CollectionName> } {
  const previous = currentTracker;
  const collections = new Set<CollectionName>();
  currentTracker = collections;
  try {
    return { result: fn(), collections };
  } finally {
    currentTracker = previous;
  }
}

/** Collections read but not declared — the reads the version-sum snapshot cannot see. */
export function undeclaredReads(
  declared: readonly CollectionName[],
  observed: ReadonlySet<CollectionName>,
): CollectionName[] {
  return [...observed].filter((name) => !declared.includes(name));
}

/**
 * Reports a compute that read a collection its dependency list doesn't declare.
 *
 * Throws under vitest so the drift fails the suite; warns in dev, where the app has to keep
 * running. Dev warnings are deduped per distinct drift rather than per compute, so a second
 * undeclared collection appearing in an already-warned compute still surfaces.
 *
 * @throws when an undeclared read is observed in test mode.
 */
export function reportUndeclaredReads(
  debugName: string,
  declared: readonly CollectionName[],
  observed: ReadonlySet<CollectionName>,
): void {
  const undeclared = undeclaredReads(declared, observed);
  if (undeclared.length === 0) return;
  const message = `${debugName} reads collections missing from its dependency list: ${undeclared.join(", ")}`;
  if (throwOnUndeclaredRead) throw new Error(message);
  if (warnedDrifts.has(message)) return;
  warnedDrifts.add(message);
  console.warn(message);
}
